/**
 * Browser entrypoint. Wiring order matters:
 *
 *   1. `./i18n` side-effect import — populates the i18next singleton so
 *      `t(...)` calls during first render are not "key returned as string".
 *   2. `./index.css` — Tailwind layers + design tokens (imported from
 *      `@cometchat-ai/ui-dashboard`).
 *   3. `<ThemeProvider>` — manages the `.dark` class on `<html>`.
 *   4. `<QueryClientProvider>` — shared TanStack Query cache. Both
 *      `<AuthProvider>` (for `/api/me`) and component-level `useQuery`
 *      calls live under it.
 *   5. `<BrowserRouter>` then `<App />`.
 *
 * Per `aidlc-docs/construction/auth-workspaces-rbac/nfr-design/frontend-architecture.md § 3`.
 */

import "./i18n";

import { clearToken } from "@cometchat-ai/auth/client";
import { ThemeProvider, Toaster } from "@cometchat-ai/ui-dashboard";
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router";

import { App } from "./App";
import "./index.css";

/**
 * True for an expired/invalid-session error (HTTP 401). Both API error
 * classes (`ApiError`, `AgentsApiError`) carry a numeric `status`; some
 * also expose `isUnauthenticated()`. Duck-typed so we don't import either
 * class here and don't misfire on plain `Error`s thrown from render.
 */
function isAuthExpiredError(error: unknown): boolean {
  if (typeof error !== "object" || error === null) return false;
  const e = error as { status?: unknown; isUnauthenticated?: unknown };
  if (typeof e.isUnauthenticated === "function") {
    return (e as { isUnauthenticated: () => boolean }).isUnauthenticated();
  }
  return e.status === 401;
}

/**
 * Centralizes the auth-expiry handling that individual fetchers used to
 * each do inline (`if (res.status === 401) clearToken()`): on any query
 * or mutation that fails with 401, drop the stale token and bounce to
 * `/login`. Generic errors are intentionally NOT surfaced here —
 * components already show those inline, and a global toast would
 * double-notify.
 */
function handleAuthExpiry(error: unknown): void {
  if (!isAuthExpiredError(error)) return;
  clearToken();
  // Guard against a redirect loop: the login page (and other public auth
  // routes) can legitimately 401 while logged out — don't redirect onto
  // ourselves from there.
  const path = window.location.pathname;
  if (path === "/login" || path.startsWith("/auth/") || path === "/forgot-password") return;
  const redirect = encodeURIComponent(window.location.pathname + window.location.search);
  window.location.assign(`/login?redirect=${redirect}`);
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: handleAuthExpiry }),
  mutationCache: new MutationCache({ onError: handleAuthExpiry }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: true,
      retry: 1,
    },
  },
});

const rootEl = document.getElementById("root");
if (!rootEl) {
  throw new Error("Root element #root not found");
}

createRoot(rootEl).render(
  <StrictMode>
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <App />
        </BrowserRouter>
        <Toaster />
      </QueryClientProvider>
    </ThemeProvider>
  </StrictMode>,
);
